<template>
  <AppWithLoading>
    <div class="vacancy-list-page" v-if="item">
      <slot name="head" />
      <div class="vacancy-list-wrap">
        <div class="vacancy-list-company">
          <div class="vacancy-list-company__text-wrap">
            <VTextField
              v-model="item.title"
              :label="$t('title')"
              outlined
              dense
              autofocus
              :error="errorFields.title"
              hide-details
              class="vacancy-list-company__name"
            />
            <VTextarea
              v-model="item.description"
              :label="$t('description')"
              outlined
              dense
              hide-details
              :rows="$vuetify.breakpoint.mdAndDown ? 5 : 4"
              class="vacancy-list-company__description"
            />
          </div>
        </div>
        <VacancyListsDetailFilter
          @input="onFilterChange"
          v-model="item"
        >
          <VAlert color="red" v-if="errorFields.filter">
            <span style="color: white">
              {{ errorFields.filter }}
            </span>
          </VAlert>
          <div class="">
            <slot name="actions" :item="item" :validate="validate" />
          </div>
        </VacancyListsDetailFilter>
      </div>
    </div>
  </AppWithLoading>
</template>

<script>
import VacancyListsDetailFilter from '@/components/VacancyLists/VacanciesListDetailFilter';
import { ref, toRefs, watch } from '@vue/composition-api';
import AppWithLoading from '@/components/AppWithLoading';
import { vacancyListFilterType } from '@/enums';
import CompanyLogo from '@/components/Company/CompanyLogo';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'VacancyListsDetail',
  components: { CompanyLogo, AppWithLoading, VacancyListsDetailFilter },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },
  setup(props) {
    const { value } = toRefs(props);
    const item = ref(null);
    const errorFields = ref({});
    const { t } = useI18n();

    function initItem() {
      item.value = {
        type: value.value?.type || vacancyListFilterType.ACTIVE,
        title: value.value?.title || '',
        description: value.value?.description || t('calltoaction'),
        ids: value.value?.listData?.ids || [],
        excludedIds: value.value?.listData?.exludedIds || [],
      };
    }

    initItem();

    watch(value, () => {
      initItem();
    });

    function onFilterChange({ ids, type }) {
      item.value.ids = ids;
      item.value.type = type;
    }

    watch(() => item.value.type, () => {
      errorFields.value = {};
    });

    function validate() {
      errorFields.value = {};

      if (!item.value.title) {
        errorFields.value.title = true;
      }

      if ((item.value.type === vacancyListFilterType.LIST)
        && (!item.value.ids || item.value.ids.length === 0)) {
        errorFields.value.filter = t('emptyList');
      }

      if ((item.value.type === vacancyListFilterType.ADDRESS)
        && (!item.value.ids || item.value.ids.length === 0)) {
        errorFields.value.filter = t('emptyAddress');
      }

      return Object.keys(errorFields.value).length === 0;
    }

    return {
      item,
      onFilterChange,
      validate,
      errorFields,
    };
  },
};
</script>

<style lang="scss" scoped>
  .vacancy-list-page {
    max-width: 56.25rem;
    padding-top: 1.875rem;
    margin: 0 auto;
  }
  .vacancy-list-wrap {

  }
  .vacancy-list-company {
    display: flex;
    background: #FFFFFF;
    padding: 0.75rem 1.875rem 1.1875rem 0.75rem;
    box-shadow: 0 2px 0.5rem rgba(0, 0, 0, 0.16);
    border-radius: 0.625rem;
    margin-bottom: 1.25rem;
    &__image-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 10.625rem;
      height: 10.625rem;
      padding: 1.125rem;
      background: #FFFFFF;
      border: 1px solid #EEEEEE;
      border-radius: 0.25rem;
      margin-right: 1.75rem;
      img {
        max-width: 8.375rem;
        min-height: 8.375rem;
      }
    }
    &__text-wrap {
      flex-grow: 2;
    }
    &__link {
      display: inline-block;
      font-size: 0.875rem;
      line-height: 1.43;
      color: #0A82D9;
      text-decoration: none;
      margin-top: 1.125rem;
    }
    &__name {
      color: #212121;
      margin-bottom: 1.25rem;
    }
    &__description textarea {
      font-size: 0.875rem;
      line-height: 1.43;
      color: #212121;
    }
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .vacancy-list-company {
      &__image-wrap {
        width: 10rem;
        height: 10rem;
        padding: 0.9375rem;
        margin-right: 1.625rem;
        img {
          max-width: 8.125rem;
          min-height: 8.125rem;
        }
      }
    }
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .vacancy-list-company {
      padding: 1rem 0.75rem;
      &__image-wrap {
        width: 6rem;
        height: 6rem;
        padding: 0.625rem;
        margin-right: 1rem;
        img {
          max-width: 4.75rem;
          min-height: 4.75rem;
        }
      }
      &__name {
        font-size: 1.25rem;
      }
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .vacancy-list-company {
      display: block;

      &__image-wrap {
        width: 8rem;
        height: 8rem;
        padding: 0.875rem;
        margin: 0 auto 1.25rem;
        img {
          max-width: 6.25rem;
          min-height: 6.25rem;
        }
      }
    }
  }
</style>

<i18n lang="yml" locale="ru">
  title: Название подборки (для себя)
  description: Текст с призывом пройти собеседование (для кандидата)
  titleEmpty: Укажите название подборки
  emptyList: Не выбрано ни одной вакансии
  emptyAddress: Выберите адреса
  calltoaction: Спасибо за интерес к нашим вакансиям! Мы ценим ваше время, поэтому первое знакомство можно провести в формате онлайн-интервью! Пройдите видео-собеседование на любую из этих вакансий прямо с телефона или компьютера в любое удобное для вас время. Мы рассмотрим ваше интервью и вернёмся с обратной связью.
</i18n>
<i18n lang="yml" locale="en">
  title: Title for this job selection (only you see this)
  description: Interview invitation (for candidates)
  titleEmpty: Fill in the job set title
  emptyList: No vacancies selected
  emptyAddress: Choose some addresses
  calltoaction: 'Thank you for your interest in working with us! We value your time, and our first meeting can be held online!
  Apply for any of these jobs directly from your phone or computer at any time convenient for you. We will review your application and get back to you.'
</i18n>
